import {ReactElement, useEffect, useState} from 'react'
import {BrowserRouter, Route, Routes, useNavigate} from 'react-router-dom'

import BoardPage from './pages/boardPage'
import ErrorPage from './pages/errorPage'
import LoginPage from './pages/loginPage'
import RegisterPage from './pages/registerPage'
import {useUsers} from './store/users'
import {useGlobalError} from './store/globalError'
import {bootstrap} from './store/initialLoad'
import Loading from './components/common/loading'

/**
 * Port of FBRoute's `loginRequired` guard (webapp/src/route.tsx): an unknown
 * session waits, a closed session is bounced to /error?id=not-logged-in.
 */
function RequireAuth({children}: {children: ReactElement}): ReactElement | null {
    const loggedIn = useUsers((state) => state.loggedIn)

    if (loggedIn === null) {
        return <Loading/>
    }
    if (loggedIn === false) {
        return <ErrorPage/>
    }
    return children
}

/** Port of GlobalErrorRedirect() in webapp/src/router.tsx. */
function GlobalErrorRedirect(): null {
    const globalError = useGlobalError((state) => state.globalError)
    const setGlobalError = useGlobalError((state) => state.setGlobalError)
    const navigate = useNavigate()

    useEffect(() => {
        if (globalError) {
            setGlobalError('')
            navigate(`/error?id=${encodeURIComponent(globalError)}`, {replace: true})
        }
    }, [globalError, navigate, setGlobalError])

    return null
}

export default function App(): ReactElement {
    const [ready, setReady] = useState(false)

    useEffect(() => {
        let cancelled = false
        bootstrap().catch((error) => {
            console.error('[App] bootstrap failed', error)
        }).finally(() => {
            if (!cancelled) {
                setReady(true)
            }
        })
        return () => {
            cancelled = true
        }
    }, [])

    if (!ready) {
        return <Loading/>
    }

    return (
        <BrowserRouter>
            <GlobalErrorRedirect/>
            <Routes>
                <Route path='/error' element={<ErrorPage/>}/>
                <Route path='/login' element={<LoginPage/>}/>
                <Route path='/register' element={<RegisterPage/>}/>

                <Route
                    path='/team/:teamId/:boardId?/:viewId?/:cardId?'
                    element={<RequireAuth><BoardPage/></RequireAuth>}
                />
                <Route
                    path='/board/:boardId?/:viewId?/:cardId?'
                    element={<RequireAuth><BoardPage/></RequireAuth>}
                />
                <Route
                    path='/:boardId?/:viewId?/:cardId?'
                    element={<RequireAuth><BoardPage/></RequireAuth>}
                />
            </Routes>
        </BrowserRouter>
    )
}
